import React from "react";

function SectionHeader({ eyebrow, title, description, centered = false, dark = false }) {
  return (
    <div className={centered ? "text-center" : ""}>
      {eyebrow ? (
        <div
          className={`inline-flex items-center rounded-full border border-[#E7D8AC] bg-[#FFF9EA] px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#8A6A1F] ${centered ? "" : ""}`}
        >
          {eyebrow}
        </div>
      ) : null}

      <h2 className={`mt-3 text-2xl font-semibold tracking-[-0.035em] md:text-3xl ${dark ? "text-white" : "text-slate-950"}`}>
        {title}
      </h2>

      {description ? (
        <p
          className={`mt-4 text-sm leading-7 md:text-base ${dark ? "text-slate-300" : "text-slate-600"} ${centered ? "mx-auto max-w-3xl" : "max-w-3xl"
            }`}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
}

function BrandIcon({ className = "" }) {
  return (
    <img
      src="/logos/krionex-icon.png"
      alt="Krionex"
      className={`h-5 w-5 object-contain ${className}`}
    />
  );
}

function FeatureCard({ title, description, accent = "indigo" }) {
  const accentMap = {
    indigo: "border-[#E7D8AC] bg-[#FFF9EA] text-[#7A5A17]",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-[#E7D8AC] bg-[#FFF9EA] text-[#7A5A17]",
    slate: "border-slate-200 bg-slate-50 text-slate-800",
  };

  return (
    <div className="group rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_18px_55px_rgba(15,23,42,0.06)] transition duration-200 hover:-translate-y-0.5 hover:border-[#E7D8AC] hover:shadow-[0_24px_70px_rgba(15,23,42,0.09)]">
      <div
        className={`mb-4 flex h-11 w-11 items-center justify-center rounded-2xl border text-sm font-bold shadow-sm ${accentMap[accent] || accentMap.indigo
          }`}
      >
        <BrandIcon />
      </div>

      <h3 className="text-lg font-semibold tracking-tight text-slate-950">{title}</h3>
      <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
    </div>
  );
}

function MetricCard({ label, value, note }) {
  return (
    <div className="rounded-[22px] border border-white/10 bg-white/[0.07] p-4 backdrop-blur-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.16em] text-[#E6C978]">
        {label}
      </div>
      <div className="mt-2 text-xl font-semibold tracking-tight text-white">
        {value}
      </div>
      {note ? (
        <div className="mt-2 text-sm leading-6 text-slate-300">{note}</div>
      ) : null}
    </div>
  );
}

function StepCard({ step, title, description }) {
  return (
    <div className="flex gap-4 rounded-[24px] border border-slate-200 bg-slate-50 p-5 transition duration-200 hover:-translate-y-0.5 hover:border-[#E7D8AC] hover:bg-white hover:shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#050713] text-sm font-semibold text-[#E6C978] shadow-sm">
        {step}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-950">{title}</h3>
        <p className="mt-1.5 text-sm leading-6 text-slate-600">
          {description}
        </p>
      </div>
    </div>
  );
}

function Pill({ children }) {
  return (
    <div className="rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm font-medium text-white">
      {children}
    </div>
  );
}

function AudienceCard({ title, description }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-5">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
    </div>
  );
}

function TrustPoint({ title, description }) {
  return (
    <div className="flex gap-4">
      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-sm font-semibold text-white">
        ✓
      </div>
      <div>
        <div className="text-sm font-semibold text-white">{title}</div>
        <div className="mt-1 text-sm leading-6 text-slate-300">
          {description}
        </div>
      </div>
    </div>
  );
}

export default function About() {
  return (
    <div className="space-y-8">
      {/* HERO */}
      <section className="relative overflow-hidden rounded-[32px] border border-[#1F2937] bg-[#050713] shadow-[0_24px_80px_rgba(15,23,42,0.18)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.22),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_28%)]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E6C978]/60 to-transparent" />
        <div className="relative grid gap-6 px-6 py-8 lg:grid-cols-[1.15fr_0.85fr] lg:px-8 lg:py-9">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E6C978]/30 bg-[#E6C978]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#F8E7B0]">
              <BrandIcon className="h-4 w-4" />
              About KRIONEX
            </div>

            <h1 className="mt-4 max-w-3xl text-3xl font-semibold leading-tight tracking-[-0.04em] text-white lg:text-4xl">
              The operating layer for EVM token contracts.
            </h1>

            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300">
              Krionex helps builders launch ERC20, ERC721, and ERC1155 contracts across supported EVM networks, then manage token actions, track verification, review deployment history, and monitor activity from one wallet-signed workspace.
            </p>

            <div className="mt-6 flex flex-wrap gap-2.5">
              <Pill>ERC20 · ERC721 · ERC1155</Pill>
              <Pill>Multi-network deployment</Pill>
              <Pill>Wallet-signed execution</Pill>
              <Pill>Verification + operations</Pill>
            </div>
          </div>

          <div className="grid gap-4 self-start">
            <MetricCard
              label="Core Scope"
              value="Launch + Manage + Monitor"
              note="From token setup and deployment to verification, dashboard monitoring, and post-launch actions."
            />
            <MetricCard
              label="Execution Model"
              value="Non-custodial"
              note="Transactions are signed and broadcast directly from the user wallet."
            />
            <MetricCard
              label="Network Coverage"
              value="Mainnet and Testnet support"
              note="Deploy to selected supported EVM networks, including mainnet and testnet environments where available."
            />
          </div>
        </div>
      </section>

      {/* OVERVIEW + SUPPORT */}
      <section className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
          <SectionHeader
            eyebrow="Platform overview"
            title="Why Krionex exists"
            description="Token launch and post-deployment workflows are still fragmented across tools, scripts, explorers, dashboards, and manual records."
          />

          <div className="mt-6 space-y-4 text-sm leading-7 text-slate-600">
            <p>
              Launching a token contract should not require switching between multiple tools just to configure the token, run preflight checks, deploy through a wallet, verify the source, monitor the token, and track what happened after the transaction settles.
            </p>

            <p>
              Krionex brings deployment, verification, and post-launch operations into a single workflow, reducing complexity and improving reliability.
            </p>

            <p>
              The result is a cleaner, more reliable process for deploying and
              operating token contracts without turning every release into a
              custom deployment exercise.
            </p>
          </div>
        </div>

        <div className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
          <SectionHeader
            eyebrow="Platform support"
            title="Built around real launch workflows"
            description="Krionex is designed for the contract deployment and token management flows users actually need."
          />

          <div className="mt-6 grid gap-3">
            {[
              "Deploy ERC20, ERC721, and ERC1155 contracts",
              "Deploy across multiple supported networks in a single flow",
              "Run preflight simulations to catch issues before deployment",
              "Support both mainnet and testnet workflows",
              "Track automatic contract verification status",
              "Manage minting and token actions after deployment",
              "Import existing contracts into the workspace",
              "Review deployment history and verification records",
              "Access token dashboards, analytics, and liquidity visibility where data is available",
            ].map((item) => (
              <div
                key={item}
                className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM BOUNDARIES */}
      <section className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
        <SectionHeader
          eyebrow="Platform boundaries"
          title="What Krionex does and does not do"
          description="Krionex gives users deployment and management infrastructure, but users remain responsible for the contract settings and assets they deploy."
        />

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
            <h3 className="text-sm font-semibold text-emerald-900">
              Krionex helps with
            </h3>
            <ul className="mt-3 space-y-2 text-sm leading-6 text-emerald-900/80">
              <li>• Contract configuration and wallet-signed deployment</li>
              <li>• ERC20, ERC721, ERC1155, and supported custom Solidity workflows</li>
              <li>• Verification status, deployment records, dashboards, ownership, roles, and token actions</li>
              <li>• Normal dashboard reads and manual Refresh for supported live data</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-[#E7D8AC] bg-[#FFF9EA] p-5">
            <h3 className="text-sm font-semibold text-[#6F5318]">
              Users are responsible for
            </h3>
            <ul className="mt-3 space-y-2 text-sm leading-6 text-[#6F5318]/85">
              <li>• NFT images, ERC721 metadata JSON, and ERC1155 metadata JSON</li>
              <li>• Uploading metadata to IPFS, Arweave, or stable HTTPS storage</li>
              <li>• Reviewing contract settings, owner address, roles, supply, and wallet prompts</li>
              <li>• Legal, tax, regulatory, IP, marketplace, and community responsibilities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
        <SectionHeader
          eyebrow="Capabilities"
          title="What you can do with Krionex"
          description="The platform combines deployment tooling, token operations, and workspace controls into one product experience."
        />

        <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          <FeatureCard
            title="Multichain deployment"
            description="Deploy a contract across multiple supported networks from a single configuration."
            accent="indigo"
          />
          <FeatureCard
            title="Preflight Simulation"
            description="Simulate deployments before execution to catch configuration and runtime issues."
            accent="amber"
          />
          <FeatureCard
            title="Explorer verification"
            description="Track verification status after deployment so contract source visibility is easier to manage on supported explorers."
            accent="emerald"
          />
          <FeatureCard
            title="Token operations"
            description="Execute minting and contract actions after deployment."
            accent="amber"
          />
          <FeatureCard
            title="Token dashboards"
            description="Inspect token details, balances, deployment metadata, and connected operational information in one place."
            accent="slate"
          />
          <FeatureCard
            title="Analytics and liquidity visibility"
            description="Surface token metrics, dashboard insights, and liquidity-related visibility through connected dashboard views."
            accent="indigo"
          />
          <FeatureCard
            title="Workspace account controls"
            description="Use plan-based access, deployment limits, admin privileges, and support workflows inside the product."
            accent="emerald"
          />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
        <SectionHeader
          eyebrow="How it works"
          title="A structured deployment workflow"
          description="A structured workflow from configuration to deployment and post-launch operations."
          centered
        />

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <StepCard
            step="01"
            title="Configure contract"
            description="Choose the token standard, token details, deployment options, and supported feature modules."
          />
          <StepCard
            step="02"
            title="Select networks"
            description="Deploy to one or multiple supported networks depending on whether you are testing or launching live."
          />
          <StepCard
            step="03"
            title="Run Preflight Simulation"
            description="Simulate deployment conditions before sending the real wallet transaction."
          />
          <StepCard
            step="04"
            title="Sign with wallet"
            description="The backend prepares the transaction, while the user wallet signs and broadcasts it directly."
          />
          <StepCard
            step="05"
            title="Verify and manage"
            description="Track deployment status, verification results, and continue with token actions and dashboard operations."
          />
        </div>
      </section>

      {/* WHO + TRUST */}
      <section className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        <div className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
          <SectionHeader
            eyebrow="Who it's for"
            title="Built for anyone deploying smart contracts"
            description="Krionex is for solo builders, startups, and teams that want a more structured way to launch and manage contracts."
          />

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <AudienceCard
              title="Solo developers"
              description="Launch tokens, test on supported testnets, and manage deployments without relying on fragmented workflows."
            />
            <AudienceCard
              title="Builders"
              description="Create ERC20, NFT, and multi-token projects with a cleaner flow from contract setup to post-launch actions."
            />
            <AudienceCard
              title="Startups"
              description="Deploy across multiple networks while keeping verification, history, and operations connected in unified workspace."
            />
            <AudienceCard
              title="Teams"
              description="Use more repeatable deployment workflows with clearer operational visibility and shared product structure."
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl bg-[linear-gradient(135deg,#0f172a_0%,#1e1b4b_55%,#0f172a_100%)] p-8 shadow-sm">
          <SectionHeader
            eyebrow="Trust model"
            title="Non-custodial by design"
            description="All transactions are signed and executed directly from the user wallet. Krionex does not hold private keys or custody of funds."
            dark
          />

          <div className="mt-6 space-y-5">
            <TrustPoint
              title="Wallet-controlled execution"
              description="Every deployment and token action is signed and broadcast directly from the user wallet."
            />
            <TrustPoint
              title="No private key custody"
              description="Krionex does not store or control private keys and does not take custody of user funds."
            />
            <TrustPoint
              title="Workflow visibility without ownership loss"
              description="The platform prepares transactions and tracks outcomes while execution and ownership remain entirely with the user."
            />
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
                Signing
              </div>
              <div className="mt-2 text-sm font-semibold text-white">
                User wallet
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
                Custody
              </div>
              <div className="mt-2 text-sm font-semibold text-white">
                None
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* POSITIONING */}
      <section className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
        <SectionHeader
          eyebrow="Positioning"
          title="A Web3 deployment and token operations platform"
          description="Krionex is designed to provide a more production-ready way to manage the full contract launch workflow across supported EVM networks."
        />

        <div className="mt-6 grid gap-5 lg:grid-cols-3">
          <FeatureCard
            title="Structured deployment workflows"
            description="More consistent than scattered scripts and manual steps, with a productized flow around launch and confirmation."
            accent="indigo"
          />
          <FeatureCard
            title="Operational visibility"
            description="Deployment history, verification state, token dashboards, and follow-up actions stay connected inside unified workspace."
            accent="slate"
          />
          <FeatureCard
            title="SaaS-ready controls"
            description="Plans, limits, admin access, and support flows make the platform usable at product scale."
            accent="emerald"
          />
        </div>
      </section>

      {/* FINAL NOTE */}
      <section className="rounded-[32px] border border-slate-200 bg-white p-8 shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
        <SectionHeader
          eyebrow="Final note"
          title="What Krionex is built to deliver"
          description="A more reliable way to deploy and manage smart contracts across networks."
        />

        <p className="mt-4 max-w-4xl text-sm leading-7 text-slate-600">
          Krionex combines multichain deployment, verification tracking,
          token operations, and workspace management into a single platform
          designed for real-world smart contract launches. It reduces
          deployment friction, improves repeatability, and gives users a more
          structured way to move from contract setup to ongoing operations.
        </p>

        <div className="mt-8 border-t border-slate-200 pt-5 text-xs text-slate-500">
          Krionex is a software platform for smart contract deployment and
          token operations. It does not provide financial or investment
          services.
        </div>
      </section>
    </div>
  );
}